import { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Navigation
    nav: {
      start: 'Start',
      contact: 'Contact',
      requestDemo: 'Request demo',
    },
    // Hero
    hero: {
      title: 'Simplified bankruptcy\nadministration',
      description: 'Insolvio streamlines bankruptcy administration with smart integrations, practical AI, and secure data management. Built to simplify for administrators and lawyers.',
    },
    // Features
    features: {
      title: 'What Insolvio offers',
      subtitle: '',
      description: 'Insolvio provides a comprehensive platform designed to simplify every step of bankruptcy administration, ensuring secure and efficient data handling.',
      aiTitle: 'AI-Powered Assistance',
      aiDescription: 'Leverage the power of AI for automated data extraction, document creation and analysis, and saving time and improving accuracy.',
      workflowTitle: 'Integrated Workflows',
      workflowDescription: 'Manage all aspects of bankruptcy administration within a single platform, from initial filing to final distribution, for seamless operations.',
      securityTitle: 'Secure Data Management',
      securityDescription: 'Protect sensitive information with advanced encryption and access controls, ensuring compliance with data privacy regulations.',
    },
    // CTA
    cta: {
      title: 'Ready to\nsimplify?',
      getStarted: 'Get Started',
    },
    // Footer
    footer: {
      copyright: 'Copyright © 2026 | Insolvio AB',
      privacy: 'Privacy notice',
      cookies: 'Cookies',
      trustCenter: 'Trust Center',
    },
    // Privacy page
    privacy: {
      title: 'Privacy Notice',
      placeholder: 'This page will contain the Privacy Notice for Insolvio. Content will be provided.',
      comingSoon: 'Privacy notice content coming soon.',
      backToHome: 'Back to Home',
    },
    // Cookies page
    cookies: {
      title: 'Cookies',
      placeholder: 'This page will contain information about cookies used by Insolvio. Content will be provided.',
      comingSoon: 'Cookie policy content coming soon.',
      backToHome: 'Back to Home',
    },
  },
  sv: {
    // Navigation
    nav: {
      start: 'Start',
      contact: 'Kontakt',
      requestDemo: 'Boka demo',
    },
    // Hero
    hero: {
      title: 'Förenklad konkurs-\nadministration',
      description: 'Insolvio effektiviserar konkursadministration med smarta integrationer, praktisk AI och säker datahantering. Byggt för att förenkla för administratörer och jurister.',
    },
    // Features
    features: {
      title: 'Vad Insolvio erbjuder',
      subtitle: '',
      description: 'Insolvio tillhandahåller en omfattande plattform utformad för att förenkla varje steg i konkursadministrationen och säkerställa säker och effektiv datahantering.',
      aiTitle: 'AI-driven assistans',
      aiDescription: 'Dra nytta av AI för automatisk dataextraktion, framtagning av dokument och analys – spara tid och öka precisionen.',
      workflowTitle: 'Integrerade arbetsflöden',
      workflowDescription: 'Hantera alla delar av konkursadministrationen i en och samma plattform, från ansökan till slutredovisning.',
      securityTitle: 'Säker datahantering',
      securityDescription: 'Skydda känslig information med avancerad kryptering och behörighetsstyrning, i enlighet med gällande dataskyddsregler.',
    },
    // CTA
    cta: {
      title: 'Redo att\nförenkla?',
      getStarted: 'Kom igång',
    },
    // Footer
    footer: {
      copyright: 'Copyright © 2026 | Insolvio AB',
      privacy: 'Integritetsmeddelande',
      cookies: 'Cookies',
      trustCenter: 'Trust Center',
    },
    // Privacy page
    privacy: {
      title: 'Integritetsmeddelande',
      placeholder: 'Denna sida kommer att innehålla integritetsmeddelandet för Insolvio. Innehåll kommer att tillhandahållas.',
      comingSoon: 'Integritetsmeddelande kommer snart.',
      backToHome: 'Tillbaka till startsidan',
    },
    // Cookies page
    cookies: {
      title: 'Cookies',
      placeholder: 'Denna sida kommer att innehålla information om cookies som används av Insolvio. Innehåll kommer att tillhandahållas.',
      comingSoon: 'Cookie-policy kommer snart.',
      backToHome: 'Tillbaka till startsidan',
    },
  },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage for saved preference
    const saved = localStorage.getItem('insolvio-language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('insolvio-language', language);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === 'en' ? 'sv' : 'en'));
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
